import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import {
  Typography,
  Box,
  Grid,
  Chip,
  Button,
} from "@material-ui/core";
import { DataGrid } from "@material-ui/data-grid";
import CopyIcon from "@material-ui/icons/FileCopy";
import Layout from "../layouts/Layout";
import { useParams } from "react-router-dom";
import AddUser from "../components/AddUser";
import queryString from "query-string"; 
import DateAndTime from "../components/DateAndTime";
import EditResellerUser from "../components/EditResellerUser";
import { isResellerManager, isSuperAdmin } from "../auth/roles";

const ResellerUsers = (props) => {
    const { manager } = queryString.parse(props.location.search);
  const { reseller_uuid } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const [clientName, setClientName] = useState();
  const [users, setUsers] = useState([]);
  const [sortModel, setSortModel] = React.useState();
  const [rowsCount, setRowsCount] = useState();
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(100);
  const [currentUser, setCurrentUser] = useState(null);
  const [requestingApiKey, setRequestingApiKey] = useState(false);

  const fetchClientDetails = () => {
    setLoading(true);
    axios
      .get(`/reseller/${reseller_uuid}`)
      .then(({ data }) => {
        setClientName(data.name);
      })
      .catch((error) => {
        setError(error.response.data.detail);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchClientUsers = useCallback(() => {
    setLoading(true);
    axios
      .get(`/reseller/${reseller_uuid}/users`, {
        params: { page, size: pageSize },
      })
      .then(({ data }) => {
        setUsers(data.items);
        setRowsCount(data.total);
      })
      .catch((error) => {
        setError(error.response.data.detail);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [reseller_uuid, page, pageSize]);

  const viewingOwnReseller = useMemo(() => {
    if (!currentUser || !isResellerManager()) return false;
    // Check if the current user's reseller_uuid matches the reseller being viewed
    return currentUser.reseller_uuid === reseller_uuid;
  }, [currentUser, reseller_uuid]);

  const handleRequestApiKey = useCallback(() => {
    setRequestingApiKey(true);
    axios
      .post("/auth/request-api-key")
      .then(() => {
        // Refetch users after requesting API key
        fetchClientUsers();
      })
      .catch((error) => {
        setError(error.response?.data?.detail || "Failed to request API key");
      })
      .finally(() => {
        setRequestingApiKey(false);
      });
  }, [fetchClientUsers]);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        field: "full_name",
        headerName: "Full Name",
        flex: 2,
        renderCell: (params) => (
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <Grid container justifyContent="flex-start" alignItems="center">
                <Grid item>
                  <Typography>{params.row.full_name} &nbsp; </Typography>
                </Grid>

                {params.row.is_reseller_manager && (
                  <Grid item>
                    <Chip color="primary" label="Reseller Manager"></Chip>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid item>
              <EditResellerUser
                userInfo={params.row}
                setLoading={setLoading}
                callOnSuccess={fetchClientUsers}
              />
            </Grid>
          </Grid>
        ),
      },
      {
        field: "email",
        headerName: "Email",
        flex: 1,
      },
      {
        field: "last_updated_at",
        headerName: "Last Updated At",
        flex: 1,
        renderCell: (params) => (
          <DateAndTime dateAndTime={params.row.last_updated_at} />
        ),
      },
    ];

    if (isSuperAdmin() || isResellerManager()) {
      baseColumns.push({
        field: "api_key_requested",
        headerName: "API Requested",
        flex: 1,
        renderCell: (params) => (
          <Typography>{params.row.api_key_requested ? "True" : "False"}</Typography>
        ),
      });
    }

    if (viewingOwnReseller) {
      baseColumns.push({
        field: "api_key",
        headerName: "API Key",
        flex: 2,
        renderCell: (params) => {
          const isCurrentUserRow = currentUser && currentUser.uuid === params.row.uuid;
          if (!isCurrentUserRow) {
            return <Typography>-</Typography>;
          }
          
          if (params.row.api_key) {
            return (
              <Box display="flex" alignItems="center">
                <Button
                  size="small"
                  onClick={() => {
                    navigator.clipboard.writeText(params.row.api_key);
                  }}
                  style={{ minWidth: 0, padding: 4, marginRight: 6 }}
                  title="Copy API Key"
                >
                  <CopyIcon />
                </Button>
                <Typography style={{ fontFamily: "monospace" }}>
                  {params.row.api_key}
                </Typography>
              </Box>
            );
          }
          
          return (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={handleRequestApiKey}
              disabled={requestingApiKey}
            >
              {requestingApiKey ? "Requesting..." : "Request Api Key"}
            </Button>
          );
        },
      });
    }

    return baseColumns;
  }, [currentUser, requestingApiKey, handleRequestApiKey, fetchClientUsers, viewingOwnReseller]);

  useEffect(() => {
    // Get current user data from localStorage
    const userData = localStorage.getItem("userData");
    if (userData) {
      setCurrentUser(JSON.parse(userData));
    }
    fetchClientDetails();
    fetchClientUsers();
    // eslint-disable-next-line
  }, [reseller_uuid, fetchClientUsers]);

  return (
    <Layout
      loading={loading}
      error={error}
    >
      <Box mb={2} pt={2}>
        <Grid container justifyContent="space-between">
          <Grid item>
            <Typography variant="h5" align="left">
              Manage Users {!manager && `for ${clientName}`}
            </Typography>
          </Grid>

          <Grid item>
            <AddUser refreshUsers={fetchClientUsers} />
          </Grid>
        </Grid>
      </Box>

      {users.length === 0 && (
        <Box pb={2}>
          <Typography variant="h6" align="center">
            No Users.
          </Typography>
        </Box>
      )}

      {users.length !== 0 && (
        <Box pt={4}>
          <DataGrid
            rows={users}
            columns={columns}
            disableSelectionOnClick
            autoHeight
            sortModel={sortModel}
            onSortModelChange={(model) => setSortModel(model)}
            onPageChange={(newPage) => setPage(newPage + 1)}
            pageSize={pageSize}
            paginationMode="server"
            rowCount={rowsCount}
            initialState={{
              pagination: {
                page: 1,
              },
            }}
            getRowId={(row) => row.uuid}
            onPageSizeChange={setPageSize}
          />
        </Box>
      )}
    </Layout>
  );
};
export default ResellerUsers;
